import React from 'react';
import TextField from 'components/FormElements/AppTextInput';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import { useGetRole } from 'hooks/ui/useGetRole';
import FieldRow from 'components/FormElements/FieldRow';
import { FirmSelector } from 'components/FirmSelector';
import AssociatedFirms from 'components/FormElements/AssociatedFirms';
import { AdviserEditLevel } from 'utils/adviserPermissions';
import { ErrorText } from '.';

type Props = {
  isBeingEdited: boolean;
  adviserData: any;
  setAdviserData: (adviserData: any) => void;
  parentOrganisations: Array<{ _id: string; name: string }>;
  errorText: ErrorText;
  editLevel: AdviserEditLevel;
};

const BasicDetailsInput: React.FC<Props> = ({
  isBeingEdited,
  adviserData,
  setAdviserData,
  parentOrganisations,
  errorText,
  editLevel,
}) => {
  const { isSuperAdmin } = useGetRole();
  const disableInput = isBeingEdited && editLevel === AdviserEditLevel.None;

  const onFirmChange = (firmId: string) => {
    if (
      adviserData?.changeToInitialInvestmentFees.length === 1 &&
      !adviserData?.changeToInitialInvestmentFees[0].firmId
    ) {
      setAdviserData({
        ...adviserData,
        firmId,
        changeToInitialInvestmentFees: [
          { ...adviserData.changeToInitialInvestmentFees[0], firmId },
        ],
      });
    } else {
      setAdviserData({ ...adviserData, firmId });
    }
  };

  return (
    <>
      {!isBeingEdited && isSuperAdmin && (
        <FieldRow title="Firm*" centerTitle>
          <FirmSelector
            firmId={adviserData?.firmId}
            onChange={onFirmChange}
            placeholder="Select firm"
            error={!!errorText.errorValue.firmId}
            helperText={errorText.errorValue.firmId}
            required
          />
        </FieldRow>
      )}
      {isBeingEdited && isSuperAdmin && (
        <AssociatedFirms
          title="Associated firms"
          associatedFirmIds={adviserData?.associatedFirms ?? []}
          onChange={(firmIds) =>
            setAdviserData({ ...adviserData, associatedFirms: firmIds })
          }
        />
      )}
      <FieldRow
        title="Select organisation"
        informationText="(if applicable)"
        centerTitle
      >
        <Autocomplete
          id="name"
          disabled={isBeingEdited && editLevel !== AdviserEditLevel.All}
          options={parentOrganisations}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => option._id}
          filterSelectedOptions
          value={
            adviserData?.organisationData.organisationId
              ? adviserData.organisationData
              : null
          }
          onChange={(_, newValue) => {
            setAdviserData({
              ...adviserData,
              organisationData: {
                name: newValue?.name,
                organisationId: newValue?._id,
              },
            });
          }}
          onReset={() => {
            setAdviserData({
              ...adviserData,
              organisationData: {
                name: null,
                organisationId: null,
              },
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select organisation (if applicable)"
            />
          )}
        />
      </FieldRow>
      <FieldRow title="First name" centerTitle>
        <TextField
          disabled={disableInput}
          name="firstName"
          placeholder="Enter first name"
          fullWidth
          value={adviserData?.firstName}
          error={!!errorText.errorValue.firstName}
          helperText={errorText.errorValue.firstName}
          onChange={(event) =>
            setAdviserData({
              ...adviserData,
              firstName: event.target.value,
            })
          }
        />
      </FieldRow>
      <FieldRow title="Middle name" centerTitle>
        <TextField
          disabled={disableInput}
          name="middleName"
          placeholder="Enter middle name"
          fullWidth
          value={adviserData?.middleName}
          onChange={(event) =>
            setAdviserData({
              ...adviserData,
              middleName: event.target.value,
            })
          }
        />
      </FieldRow>
      <FieldRow title="Surname" centerTitle>
        <TextField
          disabled={disableInput}
          name="lastName"
          placeholder="Enter surname"
          fullWidth
          value={adviserData?.lastName}
          error={!!errorText.errorValue.lastName}
          helperText={errorText.errorValue.lastName}
          onChange={(event) =>
            setAdviserData({
              ...adviserData,
              lastName: event.target.value,
            })
          }
        />
      </FieldRow>
      <FieldRow title="Email address*" centerTitle>
        <TextField
          required
          disabled={disableInput}
          name="email"
          placeholder="Enter email"
          fullWidth
          value={adviserData?.email}
          error={!!errorText.errorValue.email}
          helperText={errorText.errorValue.email}
          onChange={(event) =>
            setAdviserData({
              ...adviserData,
              email: event.target.value,
            })
          }
        />
      </FieldRow>
      <FieldRow title="Phone number" centerTitle>
        <TextField
          disabled={disableInput}
          name="phoneNumber"
          placeholder="Enter phone number"
          fullWidth
          value={adviserData?.phoneNumber}
          error={!!errorText.errorValue.phoneNumber}
          helperText={errorText.errorValue.phoneNumber}
          onChange={(event) =>
            setAdviserData({
              ...adviserData,
              phoneNumber: event.target.value,
            })
          }
        />
      </FieldRow>
      <FieldRow title="Personal FCA number" centerTitle>
        <TextField
          disabled={disableInput}
          name="fcaNumber"
          placeholder="Enter FCA number"
          fullWidth
          value={adviserData?.fcaNumber}
          error={!!errorText.errorValue.fcaNumber}
          helperText={errorText.errorValue.fcaNumber}
          onChange={(event) =>
            setAdviserData({
              ...adviserData,
              fcaNumber: event.target.value.toUpperCase(),
            })
          }
        />
      </FieldRow>
    </>
  );
};

export default BasicDetailsInput;
